import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';
import { Skeleton } from '@/components/ui/daisy';
import useScheduledLessonHomework from '@/data/hook/scheduled-lesson-homework/useScheduledLessonHomework';

import ScheduledLessonHomeworkCard from './ScheduledLessonHomeworkCard';
import ScheduledLessonHomeworkForm from './ScheduledLessonHomeworkForm';

export type ScheduledLessonHomeworkProps = {
  scheduledLessonId: number;
  homeworkActivityId: number | null;
  hasHomeworkActivity: boolean;
};

export default function ScheduledLessonHomework({
  scheduledLessonId,
  homeworkActivityId,
  hasHomeworkActivity,
}: ScheduledLessonHomeworkProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const [isFormOpen, setIsFormOpen] = useState(false);

  const {
    homework,
    isLoading,
    createHomework,
    isCreating,
    updateHomework,
    isUpdating,
    deleteHomework,
    isDeleting,
  } = useScheduledLessonHomework({
    scheduledLessonId,
    homeworkActivityId,
    hasHomeworkActivity,
  });

  const handleSubmit = async (content: string) => {
    if (homework) await updateHomework({ id: homework.id, content });
    else await createHomework(content);
    setIsFormOpen(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-semibold leading-7 text-base">{t('homework')}</h2>

      <ConditionalRenderer condition={isLoading}>
        <Skeleton className="bg-primary-content rounded-2xl h-30" />
      </ConditionalRenderer>

      <ConditionalRenderer condition={isFormOpen}>
        <ScheduledLessonHomeworkForm
          defaultContent={homework?.content}
          isLoading={isCreating || isUpdating}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={!!homework && !isFormOpen}>
        {homework && (
          <ScheduledLessonHomeworkCard
            homework={homework}
            isDeleting={isDeleting}
            onEdit={() => setIsFormOpen(true)}
            onDelete={() => deleteHomework(homework.id)}
          />
        )}
      </ConditionalRenderer>

      <ConditionalRenderer condition={!homework && !isFormOpen}>
        <Button
          color="primary"
          size="md"
          variant="soft"
          className="w-fit"
          disabled={!hasHomeworkActivity}
          onClick={() => setIsFormOpen(true)}
        >
          <PlusIcon className="size-4" />
          {t('createHomework')}
        </Button>
      </ConditionalRenderer>
    </div>
  );
}
